import React from "react";
import { Info, Mail, Phone, MessageSquare, Clock } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Conversation, FormField, Message } from "@/types/inbox.types";
import { ConversationDetailsSkeleton } from "@/components/ui/skeleton";

interface ConversationInfoPanelProps {
  selectedConversationId: string | null;
  loadingConversation: boolean;
  conversationInfo: Conversation | null;
  conversationFormFields: FormField[];
  conversationMessages: Message[];
}

export const ConversationInfoPanel = ({
  selectedConversationId,
  loadingConversation,
  conversationInfo,
  conversationFormFields,
  conversationMessages,
}: ConversationInfoPanelProps) => {
  return (
    <div
      className={`${
        selectedConversationId
          ? "hidden lg:flex lg:w-[30%]"
          : "hidden lg:flex lg:w-[30%]"
      } flex-col border-l bg-white`}
    >
      {!selectedConversationId ? (
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <div>
            <Info className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium mb-1 text-slate-800">
              Conversation Details
            </h3>
            <p className="text-slate-500 text-sm max-w-md">
              Select a conversation to view details
            </p>
          </div>
        </div>
      ) : loadingConversation ? (
        <ConversationDetailsSkeleton />
      ) : (
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-auto min-h-0">
            {/* User Info Section */}
            <div className="p-6 border-b">
              {/* User Name */}
              <h2 className="text-[16px] font-semibold text-slate-900 mb-3">
                {conversationInfo?.user_name ||
                  conversationInfo?.user_id ||
                  "Anonymous User"}
              </h2>

              {/* Contact Details */}
              <div className="space-y-2 mb-4">
                {/* Email */}
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-500" />
                  <span className="text-sm text-slate-800">
                    {conversationInfo?.user_email || (
                      <span className="text-gray-400">Not provided</span>
                    )}
                  </span>
                </div>

                {/* Phone */}
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-500" />
                  <span className="text-sm text-slate-800">
                    {conversationInfo?.user_phone || (
                      <span className="text-gray-400">Not provided</span>
                    )}
                  </span>
                </div>
              </div>

              {/* User Type Tag */}
              {conversationInfo?.user_type && (
                <div className="mb-4">
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium capitalize ${
                      conversationInfo.user_type === "client"
                        ? "bg-blue-100 text-blue-800"
                        : conversationInfo.user_type === "lead"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-slate-100 text-slate-800"
                    }`}
                  >
                    {conversationInfo.user_type}
                  </span>
                </div>
              )}
            </div>

            {/* Conversation Insights Section */}
            <div className="p-6 border-b">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">
                Conversation Insights
              </h3>

              <div className="space-y-4">
                {/* Priority */}
                {conversationInfo?.priority && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Priority</p>
                    <span
                      className={`inline-block px-2 py-1 rounded-md text-xs font-medium capitalize ${
                        conversationInfo.priority === "urgent"
                          ? "bg-red-100 text-red-800"
                          : conversationInfo.priority === "high"
                            ? "bg-orange-100 text-orange-800"
                            : conversationInfo.priority === "medium"
                              ? "bg-yellow-100 text-yellow-800"
                              : conversationInfo.priority === "low"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {conversationInfo.priority}
                    </span>
                  </div>
                )}

                {/* Sentiment */}
                {conversationInfo?.sentiment &&
                  conversationInfo.sentiment !== "unknown" && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Sentiment</p>
                      <span
                        className={`inline-block px-2 py-1 rounded-md text-xs font-medium capitalize ${
                          conversationInfo.sentiment === "positive"
                            ? "bg-green-100 text-green-800"
                            : conversationInfo.sentiment === "negative"
                              ? "bg-red-100 text-red-800"
                              : conversationInfo.sentiment === "neutral"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {conversationInfo.sentiment}
                      </span>
                    </div>
                  )}

                {/* Intent */}
                {conversationInfo?.intent && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Intent</p>
                    <p className="text-sm text-slate-800">
                      {conversationInfo.intent}
                    </p>
                  </div>
                )}

                {/* Next Steps */}
                {conversationInfo?.next_step && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Next Steps</p>
                    <p className="text-sm text-slate-800">
                      {conversationInfo.next_step}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Conversation Summary Section */}
            {conversationInfo?.summary && (
              <div className="p-6 border-b">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">
                  Conversation Summary
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {conversationInfo.summary}
                </p>
              </div>
            )}

            {/* Tags Section */}
            {conversationInfo?.tags && conversationInfo.tags.length > 0 && (
              <div className="p-6 border-b">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {conversationInfo.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md font-medium border-0"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Form Fields Section */}
            {conversationFormFields.length > 0 && (
              <div className="p-6 border-b">
                {/* Pre-Session Fields */}
                {conversationFormFields.some(
                  (field) => field.field_type === "pre-session",
                ) && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">
                      Pre-Chat Information
                    </h3>
                    <div className="space-y-3">
                      {conversationFormFields
                        .filter((field) => field.field_type === "pre-session")
                        .map((field) => (
                          <div
                            key={field.field_id}
                            className="bg-blue-50 rounded-md p-3 border border-blue-100"
                          >
                            <h4 className="text-xs font-medium text-blue-700 mb-1">
                              {field.title}
                            </h4>
                            <p className="text-sm text-slate-800 break-words">
                              {field.value}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* In-Conversation Fields */}
                {conversationFormFields.some(
                  (field) =>
                    field.field_type === "in_conversation" || !field.field_type,
                ) && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">
                      Conversation Fields
                    </h3>
                    <div className="space-y-3">
                      {conversationFormFields
                        .filter(
                          (field) =>
                            field.field_type === "in_conversation" ||
                            !field.field_type,
                        )
                        .map((field) => (
                          <div
                            key={field.field_id}
                            className="bg-slate-50 rounded-md p-3"
                          >
                            <h4 className="text-xs font-medium text-slate-600 mb-1">
                              {field.title}
                            </h4>
                            <p className="text-sm text-slate-800 break-words">
                              {field.value}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Post-Conversation Fields */}
                {conversationFormFields.some(
                  (field) => field.field_type === "post_conversation",
                ) && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">
                      Extracted Information
                    </h3>
                    <div className="space-y-3">
                      {conversationFormFields
                        .filter(
                          (field) => field.field_type === "post_conversation",
                        )
                        .map((field) => (
                          <div
                            key={field.field_id}
                            className="bg-purple-50 rounded-md p-3 border border-purple-100"
                          >
                            <h4 className="text-xs font-medium text-purple-700 mb-1">
                              {field.title}
                            </h4>
                            <p className="text-sm text-slate-800 break-words">
                              {field.value}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Messages Count & Time Saved Section */}
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4">
                {/* Messages Count */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <MessageSquare className="h-4 w-4 text-slate-500" />
                    <p className="text-xs text-slate-500">Messages</p>
                  </div>
                  <p className="text-lg font-semibold text-slate-800">
                    {conversationMessages.length}
                  </p>
                </div>

                {/* Time Saved */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Clock className="h-4 w-4 text-slate-500" />
                    <p className="text-xs text-slate-500">Time Saved</p>
                  </div>
                  <p className="text-lg font-semibold text-blue-600">
                    {conversationInfo?.estimated_time_saved
                      ? `${conversationInfo.estimated_time_saved}m`
                      : "0m"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
